import { COMPLIANCE_CONTROL_FRESHNESS, COMPLIANCE_CONTROL_MODE } from '../../constants';
import { ComplianceComponentStatus, ComplianceControlFreshness, ComplianceControlMode } from '../../types';
import { FieldProps, useFieldHelper } from 'framework/ui/field';
import { Badge, BadgeProps } from 'framework/ui/mui-builder';

interface ComplianceControlStatusFieldProps extends FieldProps<ComplianceControlMode> {
  freshnessSource: string;
  activeFailuresSource: string;
  failingComponentsSource: string;
}

/**
 * The control's derived verdict, collapsing last-check status and freshness into one signal.
 * The failing components (`failingComponents`, or the richer `activeFailures`) — not the newest
 * decision — decide whether a control is failing.
 */
const ComplianceControlStatusField = ({
  source,
  freshnessSource,
  activeFailuresSource,
  failingComponentsSource,
}: ComplianceControlStatusFieldProps) => {
  const { getValue: getMode } = useFieldHelper<ComplianceControlMode>({ source });
  const { getValue: getFreshness } = useFieldHelper<ComplianceControlFreshness>({ source: freshnessSource });
  const { getValue: getActiveFailures } = useFieldHelper<ComplianceComponentStatus[] | null>({
    source: activeFailuresSource,
  });
  const { getValue: getFailingComponents } = useFieldHelper<string[] | null>({ source: failingComponentsSource });

  const isFailing = (getActiveFailures() ?? []).length > 0 || (getFailingComponents() ?? []).length > 0;

  const view = ((): { label: string; color: BadgeProps['color'] } => {
    if (getMode() === COMPLIANCE_CONTROL_MODE.DISABLED) return { label: 'Disabled', color: 'default' };
    if (isFailing) return { label: 'Failing', color: 'error' };
    if (getFreshness() === COMPLIANCE_CONTROL_FRESHNESS.STALE) return { label: 'Stale', color: 'warning' };
    if (getFreshness() === COMPLIANCE_CONTROL_FRESHNESS.NEVER_RUN) return { label: 'Never run', color: 'default' };

    return { label: 'Passing', color: 'success' };
  })();

  return <Badge color={view.color}>{view.label}</Badge>;
};

export default ComplianceControlStatusField;
